// src/components/GlobalFunction/siteSearchIndex.js
import navConfigB2B from "../NavConfigB2B";
import { ProductsAPI } from "../../api/Product";
import { BrandsAPI } from "../../api/Brands";
import { apiGetNews } from "../../api/News";

/**
 * SehoPro internal website search index.
 *
 * Important rule:
 * Suggestions are data-driven only.
 * This file does not invent fake products, variants, brands, categories, or pages.
 */

export const SEARCH_RESULT_LIMIT = 12;

const TYPE_ORDER = {
  product: 100,
  variant: 95,
  brand: 85,
  category: 80,
  category_group: 75,
  news: 70,
  menu: 55,
  page: 50,
};

const TYPE_LABELS = {
  product: { ZH: "產品", EN: "Product", JP: "商品" },
  variant: { ZH: "產品變體", EN: "Product Variant", JP: "商品バリエーション" },
  brand: { ZH: "品牌", EN: "Brand", JP: "ブランド" },
  category: { ZH: "分類", EN: "Category", JP: "カテゴリ" },
  category_group: { ZH: "分類2", EN: "Category Group", JP: "カテゴリグループ" },
  news: { ZH: "最新資訊", EN: "News", JP: "最新情報" },
  menu: { ZH: "選單", EN: "Menu", JP: "メニュー" },
  page: { ZH: "頁面", EN: "Page", JP: "ページ" },
};

const STATIC_PAGES = [
  {
    type: "page",
    title: { ZH: "首頁", EN: "Home", JP: "ホーム" },
    subtitle: {
      ZH: "水禾專業食品衛生入口",
      EN: "SehoPro home page",
      JP: "SehoPro ホーム",
    },
    url: "/首頁",
    aliases: ["home", "homepage", "首頁", "主頁", "水禾", "seho", "sehopro"],
  },
  {
    type: "page",
    title: { ZH: "關於水禾", EN: "About SEHO", JP: "企業情報" },
    subtitle: {
      ZH: "公司資訊、企業理念、品牌與責任",
      EN: "Company information and brand background",
      JP: "会社情報とブランド背景",
    },
    url: "/關於水禾",
    aliases: [
      "about",
      "company",
      "profile",
      "history",
      "story",
      "background",
      "水禾",
      "關於",
      "公司",
      "公司介紹",
      "公司簡介",
      "沿革",
      "歷史",
    ],
  },
  {
    type: "page",
    title: { ZH: "公司簡介", EN: "Company Profile", JP: "会社概要" },
    subtitle: {
      ZH: "水禾公司介紹與品牌歷程",
      EN: "SEHO company profile and history",
      JP: "SEHO 会社概要と沿革",
    },
    url: "/公司簡介",
    aliases: [
      "company",
      "profile",
      "about",
      "history",
      "story",
      "background",
      "intro",
      "introduction",
      "公司",
      "公司簡介",
      "公司介紹",
      "企業介紹",
      "沿革",
      "歷史",
      "水禾歷史",
    ],
  },
  {
    type: "page",
    title: { ZH: "品牌資訊", EN: "Brands", JP: "ブランド情報" },
    subtitle: {
      ZH: "水禾代理與合作品牌",
      EN: "Brands represented by SEHO",
      JP: "SEHO取扱ブランド",
    },
    url: "/品牌資訊",
    aliases: [
      "brand",
      "brands",
      "seho",
      "saraya",
      "dyson",
      "hedgehog",
      "mizuka",
      "品牌",
      "品牌資訊",
      "代理品牌",
    ],
  },
  {
    type: "page",
    title: {
      ZH: "企業責任",
      EN: "Corporate Responsibility / ESG",
      JP: "企業責任 / ESG",
    },
    subtitle: {
      ZH: "永續發展、環境與社會責任",
      EN: "Sustainability, environment, and social responsibility",
      JP: "持続可能性・環境・社会責任",
    },
    url: "/企業責任",
    aliases: [
      "esg",
      "csr",
      "responsibility",
      "sustainability",
      "environment",
      "society",
      "governance",
      "永續",
      "企業責任",
      "環境",
      "社會責任",
      "治理",
    ],
  },
  {
    type: "page",
    title: { ZH: "合作資訊", EN: "Cooperation", JP: "協業情報" },
    subtitle: {
      ZH: "合作、招募與聯合開發諮詢",
      EN: "Cooperation, recruitment, and development consulting",
      JP: "協業・採用・共同開発相談",
    },
    url: "/合作資訊",
    aliases: [
      "cooperation",
      "partner",
      "partnership",
      "recruit",
      "career",
      "job",
      "consulting",
      "合作",
      "合作資訊",
      "技術合作",
      "聯合開發",
      "招募",
      "職缺",
    ],
  },
  {
    type: "page",
    title: { ZH: "聯絡水禾", EN: "Contact Us", JP: "お問い合わせ" },
    subtitle: {
      ZH: "電話、Email、地址與聯絡表單",
      EN: "Phone, email, address, and contact form",
      JP: "電話・メール・住所・お問い合わせフォーム",
    },
    url: "/聯絡水禾",
    aliases: [
      "contact",
      "contact us",
      "support",
      "help",
      "phone",
      "telephone",
      "tel",
      "email",
      "mail",
      "address",
      "location",
      "map",
      "聯絡",
      "聯絡水禾",
      "電話",
      "信箱",
      "電子郵件",
      "地址",
      "地圖",
    ],
  },
  {
    type: "page",
    title: { ZH: "最新資訊", EN: "News", JP: "最新情報" },
    subtitle: {
      ZH: "最新消息、活動資訊與衛生資訊",
      EN: "News, events, and hygiene information",
      JP: "ニュース・イベント・衛生情報",
    },
    url: "/最新資訊/",
    aliases: [
      "news",
      "latest",
      "article",
      "information",
      "最新",
      "最新資訊",
      "最新消息",
      "活動資訊",
      "衛生資訊",
      "文章",
    ],
  },
  {
    type: "page",
    title: { ZH: "最新消息", EN: "Latest News", JP: "ニュース" },
    subtitle: {
      ZH: "水禾最新消息",
      EN: "SEHO latest news",
      JP: "SEHOニュース",
    },
    url: "/最新資訊/最新消息/",
    aliases: [
      "news",
      "press",
      "press release",
      "latest news",
      "announcement",
      "公告",
      "健康",
      "最新消息",
      "新聞",
      "消息",
    ],
  },
  {
    type: "page",
    title: { ZH: "活動資訊", EN: "Events", JP: "イベント情報" },
    subtitle: {
      ZH: "活動與展會資訊",
      EN: "Events and exhibitions",
      JP: "イベント・展示会情報",
    },
    url: "/最新資訊/活動資訊/",
    aliases: [
      "event",
      "events",
      "activity",
      "exhibition",
      "活動",
      "活動資訊",
      "展覽",
      "展會",
    ],
  },
  {
    type: "page",
    title: { ZH: "衛生資訊", EN: "Hygiene Information", JP: "衛生情報" },
    subtitle: {
      ZH: "食品衛生與清潔知識",
      EN: "Food hygiene and cleaning knowledge",
      JP: "食品衛生と清掃知識",
    },
    url: "/最新資訊/衛生資訊/",
    aliases: [
      "hygiene",
      "health",
      "cleaning",
      "food safety",
      "sanitation",
      "衛生",
      "衛生資訊",
      "食品衛生",
      "清潔",
      "消毒",
    ],
  },
  {
    type: "page",
    title: { ZH: "商品資訊", EN: "Products", JP: "商品情報" },
    subtitle: {
      ZH: "全站產品、分類與專業諮詢",
      EN: "Products, categories, and consulting",
      JP: "商品・カテゴリ・専門相談",
    },
    url: "/商品資訊",
    aliases: [
      "product",
      "products",
      "catalog",
      "category",
      "categories",
      "商品",
      "商品資訊",
      "產品",
      "產品資訊",
      "分類",
      "型錄",
    ],
  },
  {
    type: "page",
    title: { ZH: "我的收藏", EN: "Favourites", JP: "お気に入り" },
    subtitle: { ZH: "已收藏的產品", EN: "Saved products", JP: "保存した商品" },
    url: "/我的收藏",
    aliases: [
      "favourite",
      "favorite",
      "saved",
      "bookmark",
      "收藏",
      "我的收藏",
      "喜愛",
      "儲存",
    ],
  },
  {
    type: "page",
    title: { ZH: "詢價", EN: "Inquiry", JP: "見積依頼" },
    subtitle: {
      ZH: "詢價車與詢價紀錄",
      EN: "Inquiry cart and history",
      JP: "見積カートと履歴",
    },
    url: "/詢價",
    aliases: [
      "inquiry",
      "quote",
      "quotation",
      "cart",
      "request",
      "購物",
      "購物車",
      "詢價",
      "報價",
      "詢價車",
      "詢價紀錄",
    ],
  },
  {
    type: "page",
    title: { ZH: "隱私權", EN: "Privacy Policy", JP: "プライバシーポリシー" },
    subtitle: {
      ZH: "隱私權政策與條款",
      EN: "Privacy policy and terms",
      JP: "プライバシーポリシーと規約",
    },
    url: "/隱私權",
    aliases: ["privacy", "policy", "terms", "隱私", "隱私權", "政策", "條款"],
  },
  {
    type: "page",
    title: { ZH: "網站地圖", EN: "Sitemap", JP: "サイトマップ" },
    subtitle: {
      ZH: "網站頁面導覽",
      EN: "Website page map",
      JP: "サイトページ案内",
    },
    url: "/網站地圖",
    aliases: [
      "sitemap",
      "site map",
      "map",
      "navigation",
      "網站地圖",
      "導覽",
      "頁面",
    ],
  },
];

const KNOWN_INTERNAL_PAGES = new Set(
  STATIC_PAGES.map((p) => normalizePath(p.url)).filter(Boolean),
);

let cachedIndexKey = "";
let cachedIndex = [];
let cachedIndexAt = 0;
let cachedIndexPromise = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
}

function safeString(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function stripHtml(value) {
  return safeString(value).replace(/<[^>]*>/g, " ");
}

function decodeRepeatedly(input, maxTimes = 3) {
  let s = safeString(input);
  for (let i = 0; i < maxTimes; i += 1) {
    try {
      const decoded = decodeURIComponent(s);
      if (decoded === s) break;
      s = decoded;
    } catch {
      break;
    }
  }
  return s;
}

function normalizeText(value) {
  return decodeRepeatedly(value)
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

function compactText(value) {
  return normalizeText(value).replace(/[\s\-_/|.,，。:：;；()[\]{}（）]+/g, "");
}

function uniqueStrings(values) {
  const out = [];
  const seen = new Set();

  for (const value of values.flat(Infinity)) {
    const s = safeString(value);
    if (!s) continue;

    const key = normalizeText(s);
    if (!key || seen.has(key)) continue;

    seen.add(key);
    out.push(s);
  }

  return out;
}

function pickLang(value, lang) {
  if (value === undefined || value === null) return "";
  if (typeof value !== "object") return safeString(value);

  const L = normalizeLang(lang);
  return (
    safeString(value[L]) ||
    safeString(value.EN) ||
    safeString(value.ZH) ||
    safeString(value.JP) ||
    safeString(value.label) ||
    safeString(value.name) ||
    safeString(value.title) ||
    ""
  );
}

function normalizePath(path) {
  const s = safeString(path);
  if (!s || /^https?:\/\//i.test(s)) return "";
  return s.replace(/\/+/g, "/").replace(/\/$/, "") || "/";
}

function isInternalHref(href) {
  const s = safeString(href);
  return !!s && s.startsWith("/") && !/^\/\//.test(s);
}

function makeCategoryUrl(hrefOrName) {
  const value = safeString(hrefOrName);
  if (!value) return "/商品資訊";
  return `/商品資訊?cat=${encodeURIComponent(value)}`;
}

function makeGroupUrl(groupID) {
  const value = safeString(groupID);
  if (!value) return "/商品資訊";
  return `/商品資訊?group=${encodeURIComponent(value)}`;
}

function getMain(product) {
  return (
    product?.main ||
    product?.Main ||
    product?.mainInfo ||
    product?.maininfo ||
    {}
  );
}

function getProductID(product) {
  const main = getMain(product);
  return safeString(
    product?.productID ||
      product?.productId ||
      product?.id ||
      product?.sku ||
      main?.productID ||
      main?.productId ||
      main?.id ||
      main?.["編號"] ||
      main?.["產品編號"],
  );
}

function getParentID(product) {
  const main = getMain(product);
  return safeString(
    product?.parentID ||
      product?.parentId ||
      product?.parent_id ||
      product?.familyID ||
      product?.familyId ||
      main?.parentID ||
      main?.parentId ||
      main?.parent_id ||
      main?.["系列編號"] ||
      main?.["商品系列編號"],
  );
}

function getProductName(product, lang) {
  const main = getMain(product);
  const L = normalizeLang(lang);

  const directByLang =
    L === "EN"
      ? product?.productNameEN ||
        product?.nameEN ||
        product?.product_name_en ||
        main?.productNameEN ||
        main?.product_name_en ||
        main?.["產品名稱_EN"] ||
        main?.["商品系列名稱_EN"]
      : L === "JP"
        ? product?.productNameJP ||
          product?.nameJP ||
          product?.product_name_jp ||
          main?.productNameJP ||
          main?.product_name_jp ||
          main?.["產品名稱_JP"] ||
          main?.["商品系列名稱_JP"]
        : product?.productNameZH ||
          product?.nameZH ||
          product?.product_name_zh ||
          product?.productName ||
          product?.name ||
          main?.productNameZH ||
          main?.product_name_zh ||
          main?.productName ||
          main?.name ||
          main?.["產品名稱"] ||
          main?.["商品系列名稱"];

  return safeString(
    directByLang ||
      product?.productName ||
      product?.name ||
      product?.ProductName ||
      product?.Name ||
      main?.name ||
      main?.productName ||
      main?.["商品名稱"] ||
      getProductID(product) ||
      getParentID(product),
  );
}

function getVariantName(product, lang) {
  const main = getMain(product);
  const L = normalizeLang(lang);

  const directByLang =
    L === "EN"
      ? product?.variantNameEN ||
        main?.["產品變體名稱_EN"] ||
        main?.variantNameEN
      : L === "JP"
        ? product?.variantNameJP ||
          main?.["產品變體名稱_JP"] ||
          main?.variantNameJP
        : product?.variantNameZH || main?.["產品變體名稱"];

  return safeString(
    directByLang ||
      product?.variantName ||
      product?.variant ||
      main?.variantName ||
      main?.variant ||
      "",
  );
}

function isSameProductCodeLikeValue(value, productID, parentID) {
  const v = normalizeText(value);
  if (!v) return true;

  const pid = normalizeText(productID);
  const parent = normalizeText(parentID);

  if (pid && v === pid) return true;
  if (parent && v === parent) return true;

  return false;
}

function getProductDisplayTitle(productName, variantName, productID, parentID) {
  const cleanProductName = safeString(productName);
  const cleanVariantName = safeString(variantName);
  const cleanProductID = safeString(productID);
  const cleanParentID = safeString(parentID);

  if (
    cleanProductName &&
    !isSameProductCodeLikeValue(cleanProductName, cleanProductID, cleanParentID)
  ) {
    return cleanProductName;
  }

  if (
    cleanVariantName &&
    !isSameProductCodeLikeValue(cleanVariantName, cleanProductID, cleanParentID)
  ) {
    return cleanVariantName;
  }

  return (
    cleanProductName || cleanVariantName || cleanProductID || cleanParentID
  );
}

function getBrandName(product) {
  const main = getMain(product);
  const brandObj =
    product?.Brand || product?.brand || main?.Brand || main?.brand;

  const value =
    product?.brandName ||
    product?.brand_name ||
    product?.BrandName ||
    main?.brandName ||
    main?.brand_name ||
    main?.["品牌名稱"] ||
    brandObj?.brandName ||
    brandObj?.brand_name ||
    brandObj?.name ||
    (typeof brandObj === "string" ? brandObj : "");

  const s = safeString(value);
  if (!s || s === "-" || normalizeText(s) === "brand") return "";
  return s;
}

function extractStringsFromValue(value, preferredKeys = []) {
  if (value === undefined || value === null) return [];
  if (typeof value === "string" || typeof value === "number")
    return [safeString(value)];

  if (Array.isArray(value)) {
    return value.flatMap((item) =>
      extractStringsFromValue(item, preferredKeys),
    );
  }

  if (typeof value === "object") {
    const keys = [
      ...preferredKeys,
      "label",
      "name",
      "title",
      "categoryName",
      "category_name",
      "categoryDisplayName",
      "group_name",
      "groupName",
      "brandName",
    ];

    return uniqueStrings(keys.map((key) => value?.[key]).filter(Boolean));
  }

  return [];
}

function getCategoryNames(product) {
  const main = getMain(product);

  return uniqueStrings([
    extractStringsFromValue(product?.category, [
      "categoryDisplayName",
      "categoryName",
      "name",
    ]),
    extractStringsFromValue(product?.categories, [
      "categoryDisplayName",
      "categoryName",
      "name",
    ]),
    product?.categoryName,
    product?.category_name,
    product?.categoryDisplayName,
    product?.categoryPath,
    product?.category_path,
    product?.categoryHref,
    main?.category,
    main?.categoryName,
    main?.category_path,
    main?.categoryHref,
    main?.["分類"],
    main?.["類別"],
  ]);
}

function getCategoryHref(product, fallbackName) {
  const main = getMain(product);

  return safeString(
    product?.categoryHref ||
      product?.category_path ||
      product?.categoryPath ||
      product?.categoryTreePath ||
      product?.category_tree_path ||
      main?.categoryHref ||
      main?.category_path ||
      main?.categoryPath ||
      main?.categoryTreePath ||
      main?.category_tree_path ||
      fallbackName,
  );
}

function getCategoryPathParts(value) {
  return safeString(value)
    .split("/")
    .map((part) => safeString(part))
    .filter(Boolean)
    .filter((part) => {
      const normalized = normalizeText(part);
      return (
        normalized !== "product" &&
        normalized !== "商品資訊" &&
        normalized !== "商品"
      );
    });
}

function getCategoryDisplayTitle(categoryName, categoryHref) {
  const pathParts = getCategoryPathParts(categoryHref);
  const fallback = safeString(categoryName);

  if (pathParts.length > 0) {
    return pathParts[pathParts.length - 1];
  }

  return fallback;
}

function getCategoryDisplaySubtitle(categoryName, categoryHref, lang) {
  const typeLabel = getSearchTypeLabel("category", lang);
  const pathParts = getCategoryPathParts(categoryHref);
  const fallback = safeString(categoryName);

  if (pathParts.length > 1) {
    return `${typeLabel} · ${pathParts.join(" / ")}`;
  }

  if (
    pathParts.length === 1 &&
    fallback &&
    normalizeText(pathParts[0]) !== normalizeText(fallback)
  ) {
    return `${typeLabel} · ${fallback}`;
  }

  return typeLabel;
}

function getProductGroupNames(product) {
  const main = getMain(product);

  return uniqueStrings([
    extractStringsFromValue(product?.productGroups, [
      "group_name",
      "groupName",
      "label",
      "name",
    ]),
    extractStringsFromValue(product?.product_groups, [
      "group_name",
      "groupName",
      "label",
      "name",
    ]),
    extractStringsFromValue(product?.categoryGroups, [
      "group_name",
      "groupName",
      "label",
      "name",
    ]),
    extractStringsFromValue(product?.groups, [
      "group_name",
      "groupName",
      "label",
      "name",
    ]),
    product?.productGroup,
    product?.product_group,
    product?.groupName,
    product?.group_name,
    product?.categoryGroup,
    product?.category_group,
    main?.productGroup,
    main?.product_group,
    main?.groupName,
    main?.group_name,
    main?.categoryGroup,
    main?.category_group,
    main?.["分類2"],
    main?.["產品群組"],
  ]);
}

function getGroupIDFromGroup(group) {
  return safeString(
    group?.group_id ||
      group?.groupID ||
      group?.id ||
      group?.key ||
      group?.slug ||
      group?.group_name ||
      group?.groupName ||
      group?.name,
  );
}

function getGroupNameFromGroup(group, lang) {
  const L = normalizeLang(lang);

  const byLang =
    L === "EN"
      ? group?.group_name_en || group?.groupNameEN || group?.nameEN || group?.EN
      : L === "JP"
        ? group?.group_name_jp ||
          group?.groupNameJP ||
          group?.nameJP ||
          group?.JP
        : group?.group_name_zh ||
          group?.groupNameZH ||
          group?.nameZH ||
          group?.ZH;

  return safeString(
    byLang ||
      group?.group_name ||
      group?.groupName ||
      group?.label ||
      group?.name ||
      getGroupIDFromGroup(group),
  );
}

function makeProductUrl(product) {
  const parentID = getParentID(product);
  const productID = getProductID(product);

  if (!parentID && !productID) return "/商品資訊";

  const parentKey = parentID || productID;
  let url = `/商品資訊/${encodeURIComponent(parentKey)}`;

  if (productID && productID !== parentKey) {
    url += `?variant=${encodeURIComponent(productID)}`;
  }

  return url;
}

function makeNewsUrl(news) {
  const slug = safeString(news?.slug || news?.post_name || news?.name);
  const type = normalizeText(
    news?.newsType || news?.type || news?.typeSlug || news?.category || "",
  );

  if (!slug) {
    if (type.includes("health") || type.includes("衛生"))
      return "/最新資訊/衛生資訊/";
    if (type.includes("event") || type.includes("活動"))
      return "/最新資訊/活動資訊/";
    return "/最新資訊/最新消息/";
  }

  if (type.includes("health") || type.includes("衛生")) {
    return `/最新資訊/衛生資訊/${encodeURIComponent(slug)}`;
  }

  return `/最新資訊/最新消息/${encodeURIComponent(slug)}`;
}

function hasProductIdentity(row) {
  if (!row || typeof row !== "object") return false;
  return !!(getProductID(row) || getParentID(row) || getProductName(row, "ZH"));
}

function flattenProductRows(input, out = [], seen = new WeakSet()) {
  if (!input) return out;

  if (Array.isArray(input)) {
    input.forEach((item) => flattenProductRows(item, out, seen));
    return out;
  }

  if (typeof input !== "object") return out;
  if (seen.has(input)) return out;
  seen.add(input);

  if (hasProductIdentity(input)) out.push(input);

  [
    "data",
    "rows",
    "items",
    "products",
    "children",
    "variants",
    "variantProducts",
  ].forEach((key) => {
    if (input?.[key]) flattenProductRows(input[key], out, seen);
  });

  return out;
}

function normalizeRows(data, keys = ["items", "rows", "data"]) {
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function makeBaseItem({
  type,
  title,
  subtitle,
  url,
  keywords = [],
  raw = null,
  priority,
}) {
  const cleanTitle = safeString(title);
  const cleanUrl = safeString(url);

  if (!cleanTitle || !cleanUrl) return null;

  return {
    id: `${type}:${cleanUrl}:${cleanTitle}`,
    type,
    title: cleanTitle,
    subtitle: safeString(subtitle),
    url: cleanUrl,
    keywords: uniqueStrings(keywords),
    raw,
    priority: Number.isFinite(priority) ? priority : TYPE_ORDER[type] || 0,
  };
}

function buildStaticItems(lang) {
  return STATIC_PAGES.map((page) =>
    makeBaseItem({
      type: page.type,
      title: pickLang(page.title, lang),
      subtitle: pickLang(page.subtitle, lang),
      url: page.url,
      keywords: [
        pickLang(page.title, "ZH"),
        pickLang(page.title, "EN"),
        pickLang(page.title, "JP"),
        pickLang(page.subtitle, "ZH"),
        pickLang(page.subtitle, "EN"),
        pickLang(page.subtitle, "JP"),
        page.aliases || [],
      ],
      raw: page,
    }),
  ).filter(Boolean);
}

function flattenNav(nodes, lang, ancestors = [], out = []) {
  if (!Array.isArray(nodes)) return out;

  for (const node of nodes) {
    const href = safeString(node?.href);
    const label = pickLang(
      { ZH: node?.label, EN: node?.EN, JP: node?.JP },
      lang,
    );
    const labels = uniqueStrings([
      node?.label,
      node?.EN,
      node?.JP,
      ancestors.map((a) => a.label),
    ]);

    if (label && isInternalHref(href)) {
      const normalizedHref = normalizePath(href);
      const isKnownPage = KNOWN_INTERNAL_PAGES.has(normalizedHref);
      const isProductCategory =
        normalizedHref.startsWith("/商品資訊/") &&
        normalizedHref !== "/商品資訊";

      if (isKnownPage || isProductCategory) {
        out.push(
          makeBaseItem({
            type: isProductCategory ? "category" : "menu",
            title: label,
            subtitle: ancestors
              .map((a) => a.label)
              .filter(Boolean)
              .join(" / "),
            url: isProductCategory
              ? makeCategoryUrl(normalizedHref)
              : normalizedHref,
            keywords: [href, normalizedHref, labels],
            raw: node,
            priority: isProductCategory ? TYPE_ORDER.category : TYPE_ORDER.menu,
          }),
        );
      }
    }

    flattenNav(node?.children, lang, [...ancestors, { label, href }], out);
  }

  return out.filter(Boolean);
}

function addRelation(map, key, value) {
  const title = safeString(key);
  if (!title) return;

  const normalized = normalizeText(title);
  if (!normalized) return;

  const prev = map.get(normalized) || { title, keywords: new Set(), rows: [] };
  prev.rows.push(value?.row);

  uniqueStrings(value?.keywords || []).forEach((keyword) => {
    prev.keywords.add(keyword);
  });

  map.set(normalized, prev);
}

function buildBrandItems(brandsRaw, brandRelations, lang) {
  const brands = Array.isArray(brandsRaw) ? brandsRaw : [];
  const used = new Set();
  const items = [];

  for (const brand of brands) {
    const name = safeString(
      brand?.brandName || brand?.name || brand?.label || brand?.brandID,
    );
    if (!name) continue;

    const key = normalizeText(name);
    used.add(key);

    const rel = brandRelations.get(key);

    items.push(
      makeBaseItem({
        type: "brand",
        title: name,
        subtitle: getSearchTypeLabel("brand", lang),
        url: "/品牌資訊",
        keywords: uniqueStrings([
          name,
          brand?.brandID,
          brand?.brandDescZH,
          brand?.brandDescEN,
          brand?.brandDescJP,
          rel ? Array.from(rel.keywords) : [],
        ]),
        raw: { brand, relatedRows: rel?.rows || [] },
        priority: TYPE_ORDER.brand,
      }),
    );
  }

  for (const [key, rel] of brandRelations.entries()) {
    if (used.has(key)) continue;

    items.push(
      makeBaseItem({
        type: "brand",
        title: rel.title,
        subtitle: getSearchTypeLabel("brand", lang),
        url: "/品牌資訊",
        keywords: Array.from(rel.keywords),
        raw: { relatedRows: rel.rows },
        priority: TYPE_ORDER.brand,
      }),
    );
  }

  return items.filter(Boolean);
}

function buildProductItems(productsRaw, productGroupsRaw, brandsRaw, lang) {
  const products = flattenProductRows(productsRaw);
  const productItems = [];
  const brandRelations = new Map();
  const categoryRelations = new Map();
  const groupRelations = new Map();

  for (const row of products) {
    const productID = getProductID(row);
    const parentID = getParentID(row);
    const productName = getProductName(row, lang);
    const variantName = getVariantName(row, lang);
    const brandName = getBrandName(row);
    const categories = getCategoryNames(row);
    const groups = getProductGroupNames(row);
    const main = getMain(row);

    const isVariant = !!productID && !!parentID && productID !== parentID;
    const type = isVariant ? "variant" : "product";

    const productKeywords = uniqueStrings([
      productID,
      parentID,
      productName,
      variantName,
      brandName,
      categories,
      groups,
      row?.description,
      row?.spec,
      row?.specification,
      main?.description,
      main?.["商品説明"],
      main?.["產品描述"],
      main?.["規格"],
      main?.["尺寸"],
      main?.["重量"],
    ]);

    const productTitle = getProductDisplayTitle(
      productName,
      variantName,
      productID,
      parentID,
    );

    const productSubtitleParts = uniqueStrings([
      getSearchTypeLabel(type, lang),
      productID ? `Item No. ${productID}` : "",
      variantName && normalizeText(variantName) !== normalizeText(productTitle)
        ? variantName
        : "",
      productName && normalizeText(productName) !== normalizeText(productTitle)
        ? productName
        : "",
      brandName,
      parentID && productID && parentID !== productID
        ? `Parent: ${parentID}`
        : "",
      categories[0],
      groups[0],
    ]);

    const item = makeBaseItem({
      type,
      title: productTitle,
      subtitle: productSubtitleParts.join(" · "),
      url: makeProductUrl(row),
      keywords: productKeywords,
      raw: row,
      priority: TYPE_ORDER[type],
    });

    if (item) productItems.push(item);

    const relationKeywords = uniqueStrings([
      productID,
      parentID,
      productName,
      variantName,
      categories,
      groups,
    ]);

    if (brandName) {
      addRelation(brandRelations, brandName, {
        row,
        keywords: relationKeywords,
      });
    }

    categories.forEach((categoryName) => {
      const categoryHref = getCategoryHref(row, categoryName);
      const categoryTitle = getCategoryDisplayTitle(categoryName, categoryHref);

      addRelation(categoryRelations, categoryTitle, {
        row,
        keywords: [...relationKeywords, categoryName, categoryHref],
      });
    });

    groups.forEach((groupName) => {
      addRelation(groupRelations, groupName, {
        row,
        keywords: relationKeywords,
      });
    });
  }

  const brandItems = buildBrandItems(brandsRaw, brandRelations, lang);

  const categoryItems = Array.from(categoryRelations.values()).map((rel) => {
    const firstRow = rel.rows?.find(Boolean);
    const categoryHref = getCategoryHref(firstRow, rel.title);
    const categoryTitle = getCategoryDisplayTitle(rel.title, categoryHref);
    const categorySubtitle = getCategoryDisplaySubtitle(
      rel.title,
      categoryHref,
      lang,
    );

    return makeBaseItem({
      type: "category",
      title: categoryTitle,
      subtitle: categorySubtitle,
      url: makeCategoryUrl(categoryHref || categoryTitle),
      keywords: Array.from(rel.keywords),
      raw: {
        title: categoryTitle,
        originalTitle: rel.title,
        categoryHref,
        rows: rel.rows,
      },
      priority: TYPE_ORDER.category,
    });
  });

  const groupItemsFromProducts = Array.from(groupRelations.values()).map(
    (rel) =>
      makeBaseItem({
        type: "category_group",
        title: rel.title,
        subtitle: getSearchTypeLabel("category_group", lang),
        url: makeGroupUrl(rel.title),
        keywords: Array.from(rel.keywords),
        raw: { title: rel.title, rows: rel.rows },
        priority: TYPE_ORDER.category_group,
      }),
  );

  const groupItemsFromApi = normalizeRows(productGroupsRaw, [
    "items",
    "rows",
    "data",
    "groups",
  ]).map((group) => {
    const groupID = getGroupIDFromGroup(group);
    const groupName = getGroupNameFromGroup(group, lang);

    return makeBaseItem({
      type: "category_group",
      title: groupName,
      subtitle: getSearchTypeLabel("category_group", lang),
      url: makeGroupUrl(groupID || groupName),
      keywords: uniqueStrings([
        groupID,
        groupName,
        group?.group_name,
        group?.groupName,
        group?.group_name_en,
        group?.group_name_jp,
        group?.description,
      ]),
      raw: group,
      priority: TYPE_ORDER.category_group,
    });
  });

  return [
    ...productItems,
    ...brandItems,
    ...categoryItems.filter(Boolean),
    ...groupItemsFromProducts.filter(Boolean),
    ...groupItemsFromApi.filter(Boolean),
  ];
}

function buildNewsItems(newsRaw, lang) {
  return normalizeRows(newsRaw, ["items", "rows", "data", "news"])
    .map((news) => {
      const L = normalizeLang(lang);

      const title =
        L === "EN"
          ? news?.title_en || news?.title_EN || news?.title
          : L === "JP"
            ? news?.title_jp || news?.title_JP || news?.title
            : news?.title || news?.title_zh || news?.title_ZH;

      const subtitle = safeString(
        news?.subtitle || news?.excerpt || news?.newsType || news?.type || "",
      );
      const contentText = stripHtml(
        news?.content || news?.content_html || news?.excerpt || "",
      );

      return makeBaseItem({
        type: "news",
        title,
        subtitle,
        url: makeNewsUrl(news),
        keywords: uniqueStrings([
          news?.slug,
          news?.title,
          news?.title_en,
          news?.title_jp,
          news?.newsType,
          news?.type,
          news?.tags,
          subtitle,
          contentText,
        ]),
        raw: news,
        priority: TYPE_ORDER.news,
      });
    })
    .filter(Boolean);
}

function getDedupeKey(item) {
  const type = safeString(item?.type);
  const url = safeString(item?.url);
  const normalizedUrl = normalizeText(normalizePath(url) || url);
  const normalizedTitle = normalizeText(item?.title);

  if ((type === "page" || type === "menu") && normalizedUrl) {
    return `page_like:${normalizedUrl}`;
  }

  if ((type === "brand" || type === "category_group") && normalizedTitle) {
    return `${type}:${normalizedTitle}`;
  }

  return `${type}:${normalizedUrl}:${normalizedTitle}`;
}

function mergeDuplicateItems(prev, item) {
  const prevType = safeString(prev?.type);
  const nextType = safeString(item?.type);

  const preferNext =
    (prevType === "menu" && nextType === "page") ||
    (!safeString(prev?.subtitle) && safeString(item?.subtitle));

  const base = preferNext ? item : prev;
  const extra = preferNext ? prev : item;

  return {
    ...base,
    keywords: uniqueStrings([base.keywords, extra.keywords]),
    raw: {
      base: base.raw,
      duplicate: extra.raw,
    },
    priority: Math.max(base.priority || 0, extra.priority || 0),
  };
}

function dedupeItems(items) {
  const map = new Map();

  for (const item of items.filter(Boolean)) {
    const key = getDedupeKey(item);
    const prev = map.get(key);

    if (!prev) {
      map.set(key, item);
      continue;
    }

    map.set(key, mergeDuplicateItems(prev, item));
  }

  return Array.from(map.values());
}

function valueFromSettled(result, fallback) {
  return result?.status === "fulfilled" ? result.value : fallback;
}

export async function buildSiteSearchIndex({
  mode = "b2b",
  lang = "ZH",
  force = false,
} = {}) {
  const L = normalizeLang(lang);
  const M = String(mode || "b2b").toLowerCase() === "b2c" ? "b2c" : "b2b";
  const key = `${M}:${L}`;
  const now = Date.now();

  if (
    !force &&
    cachedIndexKey === key &&
    cachedIndex.length &&
    now - cachedIndexAt < CACHE_TTL_MS
  ) {
    return cachedIndex;
  }

  if (!force && cachedIndexPromise && cachedIndexKey === key) {
    return cachedIndexPromise;
  }

  cachedIndexKey = key;

  cachedIndexPromise = (async () => {
    const staticItems = buildStaticItems(L);
    const navItems = flattenNav(navConfigB2B, L);

    const [
      productsResult,
      groupedProductsResult,
      groupsResult,
      brandsResult,
      newsResult,
    ] = await Promise.allSettled([
      ProductsAPI.list({ mode: M, lang: L }),
      ProductsAPI.groupList({ mode: M, lang: L }),
      ProductsAPI.productGroups({ lang: L }),
      BrandsAPI.list({ lang: L }),
      apiGetNews({ status: "published", limit: 200 }),
    ]);

    const productsRaw = [
      valueFromSettled(productsResult, []),
      valueFromSettled(groupedProductsResult, []),
    ];

    const productGroupsRaw = valueFromSettled(groupsResult, []);
    const brandsRaw = valueFromSettled(brandsResult, []);
    const newsRaw = valueFromSettled(newsResult, []);

    const dynamicItems = [
      ...buildProductItems(productsRaw, productGroupsRaw, brandsRaw, L),
      ...buildNewsItems(newsRaw, L),
    ];

    const nextIndex = dedupeItems([
      ...staticItems,
      ...navItems,
      ...dynamicItems,
    ]);

    cachedIndex = nextIndex;
    cachedIndexAt = Date.now();
    cachedIndexPromise = null;

    return nextIndex;
  })();

  try {
    return await cachedIndexPromise;
  } catch (error) {
    cachedIndexPromise = null;

    const fallback = dedupeItems([
      ...buildStaticItems(L),
      ...flattenNav(navConfigB2B, L),
    ]);
    cachedIndex = fallback;
    cachedIndexAt = Date.now();

    return fallback;
  }
}

function scoreField(queryNorm, queryCompact, value, weight) {
  const text = normalizeText(value);
  if (!text) return 0;

  const compact = compactText(text);
  let score = 0;

  if (text === queryNorm || compact === queryCompact)
    score = Math.max(score, 1000 * weight);
  if (text.startsWith(queryNorm) || compact.startsWith(queryCompact))
    score = Math.max(score, 820 * weight);
  if (text.includes(queryNorm) || compact.includes(queryCompact))
    score = Math.max(score, 460 * weight);

  const words = queryNorm.split(" ").filter(Boolean);

  if (words.length > 1) {
    const matched = words.filter(
      (word) => text.includes(word) || compact.includes(compactText(word)),
    ).length;
    if (matched)
      score = Math.max(
        score,
        (160 * matched + 60 * (matched / words.length)) * weight,
      );
  }

  return score;
}

function scoreItem(item, query) {
  const queryNorm = normalizeText(query);
  const queryCompact = compactText(query);

  if (!queryNorm || !queryCompact) return 0;

  let score = 0;

  score = Math.max(
    score,
    scoreField(queryNorm, queryCompact, item.title, 1.35),
  );
  score = Math.max(
    score,
    scoreField(queryNorm, queryCompact, item.subtitle, 0.75),
  );

  for (const keyword of item.keywords || []) {
    score = Math.max(score, scoreField(queryNorm, queryCompact, keyword, 1));
  }

  if (!score) return 0;

  const typeBoost = (TYPE_ORDER[item.type] || 0) / 10;
  return Math.round(score + typeBoost);
}

export function searchSiteIndex(
  index,
  query,
  { limit = SEARCH_RESULT_LIMIT } = {},
) {
  const q = safeString(query);

  if (!q) return [];

  return (Array.isArray(index) ? index : [])
    .map((item) => ({ ...item, score: scoreItem(item, q) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if ((b.priority || 0) !== (a.priority || 0))
        return (b.priority || 0) - (a.priority || 0);
      return a.title.localeCompare(b.title, "zh-Hant");
    })
    .slice(0, limit);
}

export function getBestSearchTarget(results, query) {
  const list = Array.isArray(results) ? results : [];

  if (!list.length) return null;

  const top = list[0];
  const second = list[1];
  const qNorm = normalizeText(query);
  const topTitle = normalizeText(top.title);
  const exactTop =
    topTitle === qNorm || compactText(topTitle) === compactText(qNorm);

  if (exactTop && top.score >= 900) return top;
  if (top.score >= 980 && (!second || top.score - second.score >= 120))
    return top;
  if (
    top.type === "page" &&
    top.score >= 850 &&
    (!second || top.score - second.score >= 80)
  )
    return top;

  return null;
}

export function getSearchResultsUrl(query) {
  const q = safeString(query);
  return q ? `/搜尋?q=${encodeURIComponent(q)}` : "/搜尋";
}

export function getSearchTypeLabel(type, lang = "ZH") {
  const L = normalizeLang(lang);
  return TYPE_LABELS[type]?.[L] || TYPE_LABELS[type]?.ZH || safeString(type);
}

export function clearSiteSearchCache() {
  cachedIndexKey = "";
  cachedIndex = [];
  cachedIndexAt = 0;
  cachedIndexPromise = null;
}
